/**
 * `/receiving` — blank-inventory receiving (Capability 1). The page opens on what is waiting at the
 * dock (the inbound board joined with our ledger); scanning an S&S carton or packing slip looks the
 * shipment up (ledger-joined, so an already-received invoice or box says so) and opens the report —
 * box by box when S&S lists the boxes' contents (`receiving-report.tsx`), the whole invoice when it
 * doesn't. Short-receive: lowering a line's "Received" records only what arrived; the shortfall stays
 * owed on the matching PO and lands in the open-shortages claim worklist below.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { AlertTriangle, ClipboardList, Loader2, PackageCheck, ScanLine, Undo2 } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { useAuth } from '@/lib/auth-state';
import { invalidateInventory } from './inventory-lib';
import { fmtDate, ReceivingReport, type ReceivingResult, SHORTAGES_KEY } from './receiving-report';
import { WaitingList } from './receiving-waiting-list';

type OpenShortage = components['schemas']['ReceivingShortagesResult']['shortages'][number];

/** react-query key for the recently-settled (undo) worklist. */
const RESOLVED_KEY = ['receiving', 'resolved'] as const;
/** react-query key for the recently-corrected (undo a mis-clicked correction) worklist. */
const CORRECTIONS_KEY = ['receiving', 'recent-corrections'] as const;

// Nested schemas aren't named components — derive from their parents.
type ResolvedShortage = components['schemas']['ReceivingResolvedListResult']['resolved'][number];
type RecentCorrection =
  components['schemas']['ReceivingRecentCorrectionsResult']['corrections'][number];
type SettleReason = components['schemas']['ReceivingResolveRequest']['reason'];
/** Manual-settle reasons for a claim Phase-1 can't auto-close (their labels drive the select). */
const SETTLE_REASONS: { value: SettleReason; label: string }[] = [
  { value: 'made-good', label: 'Made good (replacement / substitute arrived)' },
  { value: 'credited', label: 'S&S credited us (money back)' },
  { value: 'written-off', label: 'Write off (won’t recover)' },
];
const REASON_LABEL: Record<SettleReason, string> = {
  'made-good': 'made good',
  credited: 'credited',
  'written-off': 'written off',
};

/**
 * Autofocus the scan box on a pointer device only. On touch it opened the on-screen keyboard over the
 * page the moment Receiving loaded — before the operator had even picked up the scanner. Defaults to
 * TRUE where the query can't be answered (jsdom, SSR), so desktop behaviour is unchanged.
 */
const AUTOFOCUS_SCAN =
  typeof window === 'undefined' ? true : (window.matchMedia?.('(pointer: fine)')?.matches ?? true);

/** Whole days since a claim was recorded — "today" / "1d old" / "6d old". */
function claimAge(iso: string): string | null {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const days = Math.floor((Date.now() - t) / 86_400_000);
  return days <= 0 ? 'today' : `${days}d old`;
}

/**
 * Auto-clear needs BOTH stamps on the claim's receipt: the FSP PO# (ties the make-good to the claim)
 * and the S&S order number (proves the make-good is a different order, not a warehouse split of the
 * same one). Claims recorded before stamping existed can only leave via "Made good…".
 */
function willNeverAutoClear(s: OpenShortage): boolean {
  return !s.poNumber || !s.orderNumber;
}

/** Label for a shortage / on-hand-ish row that carries S&S garment names. */
function shortageLabel(s: OpenShortage): string {
  return [s.brandName, s.styleName].filter(Boolean).join(' ') || s.ssSku;
}
function shortageVariant(s: OpenShortage): string {
  return [s.colorName, s.sizeName].filter(Boolean).join(' · ');
}

/**
 * The `?code=` deep link as the exact text in the address bar. The router JSON-parses search values, so
 * a hand-typed `?code=103194160.0022` arrives as a NUMBER — which crashed the page on `.trim()` — and a
 * carton code ending in zeros would lose them (`.0010` → `.001`, no longer matching the box barcode).
 * The app's own links quote the value (`?code=%22…%22`), so read the raw query string and unquote it;
 * fall back to the router's value only if there is no query string to read.
 */
export function readDeepLinkCode(fromRouter: unknown): string {
  const raw =
    typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('code');
  if (raw != null) {
    const t = raw.trim();
    return t.length >= 2 && t.startsWith('"') && t.endsWith('"') ? t.slice(1, -1).trim() : t;
  }
  return fromRouter == null ? '' : String(fromRouter).trim();
}

export function ReceivingPage() {
  const client = useApiClient();
  // Deep-link support (P3): the Inbound board's "Receive" buttons land here with ?code=<invoice> so
  // the operator never carries an invoice number across a tab switch in their head.
  // `unknown`, not `string`: the router JSON-parses search values (see readDeepLinkCode).
  const urlSearch = useSearch({ strict: false }) as { code?: unknown };
  const [code, setCode] = useState(() => readDeepLinkCode(urlSearch.code));
  const inputRef = useRef<HTMLInputElement>(null);

  const lookup = useMutation({
    mutationFn: async (raw: string): Promise<ReceivingResult> => {
      const { data, error } = await client.GET('/receiving/lookup', {
        params: { query: { code: raw } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Lookup failed.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  // Run the deep-linked lookup once on mount (react-query keeps `mutate` stable).
  const autoRan = useRef(false);
  const autoLookup = lookup.mutate;
  useEffect(() => {
    const deepLinked = readDeepLinkCode(urlSearch.code);
    if (!autoRan.current && deepLinked) {
      autoRan.current = true;
      autoLookup(deepLinked);
    }
  }, [urlSearch.code, autoLookup]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const raw = code.trim();
    if (raw) lookup.mutate(raw);
  };

  const result = lookup.data;
  // "Scan next box": drop the report and put the cursor back in the scan field.
  const scanNext = () => {
    lookup.reset();
    setCode('');
    inputRef.current?.focus();
  };
  // A receipt just landed: re-arm a hardware scanner (pointer devices only — on touch this would
  // pop the keyboard over the success panel). The report stays on screen.
  const rearm = () => {
    if (AUTOFOCUS_SCAN) inputRef.current?.focus();
  };

  return (
    <div className="space-y-6">
      <p className="max-w-3xl text-sm text-muted-foreground">
        Scan a carton to receive that box, or the packing slip to receive the whole shipment. If
        fewer came out of a box than S&amp;S lists, lower that line’s <strong>Received</strong>{' '}
        count before confirming.
      </p>

      <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
        <div className="min-w-[16rem] flex-1 space-y-1.5">
          <Label htmlFor="receiving-code">S&amp;S invoice or carton barcode</Label>
          <div className="relative">
            <ScanLine className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="receiving-code"
              ref={inputRef}
              autoFocus={AUTOFOCUS_SCAN}
              className="pl-9"
              placeholder="e.g. 100354995.0012"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onFocus={(e) => e.currentTarget.select()}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>
        <Button type="submit" disabled={!code.trim() || lookup.isPending}>
          {lookup.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <ScanLine className="size-4" />
          )}
          Look up
        </Button>
      </form>

      {lookup.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn't look up that shipment</AlertTitle>
          <AlertDescription>
            {lookup.error instanceof Error ? lookup.error.message : 'Network error.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Key on the scanned code so a new lookup fully remounts the report (received/note edits + the
          confirm/undo state reset per scan). */}
      {result && (
        <ReceivingReport
          key={result.scannedCode}
          result={result}
          onScanNext={scanNext}
          onReceived={rearm}
        />
      )}

      {!lookup.isPending && !lookup.isError && !result && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <PackageCheck className="size-4" aria-hidden />
          Nothing scanned yet — pick a shipment below or scan a carton.
        </p>
      )}

      {/* What is waiting at the dock — the to-do. Stays under the report so the next box is one tap
          away once a receipt lands. */}
      <WaitingList
        onPick={(inv) => {
          setCode(inv);
          lookup.mutate(inv);
        }}
      />

      <OpenShortagesPanel />
      <RecentActivityPanel />
    </div>
  );
}

/**
 * The open-shortages claim worklist: lines recorded short on arrival (S&S billed, didn't deliver)
 * that haven't been reversed. Persistent across scans so the owner can chase S&S credits/reships.
 * Hidden when there are none.
 */
function OpenShortagesPanel() {
  const client = useApiClient();
  // Invoices still being received box by box: a claim on one of their lines must not be SETTLED yet — the
  // resolve marker is per line, so a short in a later box of the same line would vanish behind it.
  const inbound = useQuery({
    queryKey: ['inventory-inbound'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await client.GET('/inventory/inbound');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load inbound.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });
  const partlyReceived = new Set(
    (inbound.data?.awaitingReceipt ?? [])
      .filter((sh) => sh.boxesReceived != null && sh.invoiceNumber)
      .map((sh) => sh.invoiceNumber as string),
  );
  const shortages = useQuery({
    queryKey: SHORTAGES_KEY,
    queryFn: async () => {
      const { data, error } = await client.GET('/receiving/shortages');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load shortages.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const data = shortages.data;
  if (!data || data.shortages.length === 0) return null;

  return (
    <div className="space-y-2 rounded-lg border border-amber-300 dark:border-amber-900">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2.5 dark:border-amber-900 dark:bg-amber-950/40">
        <div className="flex items-center gap-2">
          <ClipboardList className="size-4 text-amber-700 dark:text-amber-400" />
          <span className="font-medium text-amber-900 dark:text-amber-300">
            S&amp;S owes us — open claims
          </span>
        </div>
        <span className="text-sm text-amber-800 dark:text-amber-400">
          {data.totalShortUnits} {data.totalShortUnits === 1 ? 'piece' : 'pieces'} across{' '}
          {data.shortages.length} {data.shortages.length === 1 ? 'line' : 'lines'}
        </span>
      </div>
      {/* Every rule this paragraph used to carry now sits where it applies: the auto-clear rules are
          on the rows they affect (the "won't auto-clear" notes below), and the miscount rule is in
          the confirm that button now opens. What's left is what the panel IS. */}
      <p className="px-4 text-xs text-muted-foreground">
        S&amp;S invoiced these pieces but they weren’t in the box.
      </p>
      <ul className="divide-y px-1 pb-1 text-sm">
        {data.shortages.map((s) => (
          <ShortageRow
            key={`${s.invoice}#${s.lineNumber}`}
            s={s}
            settleBlocked={partlyReceived.has(s.invoice)}
          />
        ))}
      </ul>
    </div>
  );
}

/**
 * One open-shortage row + its correction controls. "All arrived" sets received = claimed (clears the
 * shortage); the editable field sets any true count in [0, claimed]. Both call POST /receiving/correct,
 * which reconciles on-hand AND the matching PO (unlike a bare on-hand Adjust). Resolved rows drop out on
 * the refetch. Writer-gated (the server 403 is the real guard).
 */
function ShortageRow({ s, settleBlocked }: { s: OpenShortage; settleBlocked: boolean }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const { state } = useAuth();
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;
  const [received, setReceived] = useState<number>(s.receivedQty);
  const [settleOpen, setSettleOpen] = useState(false);
  // "All arrived (miscount)" declares the shortage never happened and ADDS the missing pieces to
  // stock. It was one tap on a 28px button whose only guidance was a hover `title` — which does not
  // exist on touch, where the mis-tap risk is highest.
  const [miscountOpen, setMiscountOpen] = useState(false);
  // The correction sets received to claimedQty, so THIS is what it adds to stock. `residualShort` is
  // a different number (what's owed after PO reconciliation) — see the dialog.
  const addedByMiscount = Math.max(0, s.claimedQty - s.receivedQty);
  const alreadyMadeGood = Math.max(0, addedByMiscount - s.residualShort);
  const [reason, setReason] = useState<SettleReason>('made-good');
  // Pre-fill with the time-blocked make-good's invoice when the server identified one — the exact
  // value rule 4 needs to consume those units, saving the operator a transcription (and a typo).
  const [replacementInvoice, setReplacementInvoice] = useState(s.timeBlockedInvoice ?? '');

  const correct = useMutation({
    mutationFn: async (newReceived: number) => {
      const { data, error } = await client.POST('/receiving/correct', {
        body: {
          invoice: s.invoice,
          lineNumber: s.lineNumber,
          receivedQty: newReceived,
          clientToken: crypto.randomUUID(),
        },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Correction failed.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    onSuccess: () => {
      invalidateInventory(qc);
      qc.invalidateQueries({ queryKey: SHORTAGES_KEY });
      // A correction (esp. "All arrived") now shows in the recently-corrected undo list.
      qc.invalidateQueries({ queryKey: CORRECTIONS_KEY });
    },
  });

  const settle = useMutation({
    mutationFn: async () => {
      const { data, error } = await client.POST('/receiving/resolve', {
        body: {
          invoice: s.invoice,
          lineNumber: s.lineNumber,
          ssSku: s.ssSku,
          reason,
          replacementInvoice: replacementInvoice.trim() || undefined,
        },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Settle failed.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    onSuccess: () => {
      invalidateInventory(qc);
      qc.invalidateQueries({ queryKey: SHORTAGES_KEY });
      qc.invalidateQueries({ queryKey: RESOLVED_KEY });
    },
  });

  const onEdit = (raw: string) => {
    const n = raw === '' ? 0 : Math.trunc(Number(raw));
    setReceived(Number.isFinite(n) ? Math.min(Math.max(0, n), s.claimedQty) : 0);
  };

  return (
    <li className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-3 py-2">
      <span className="min-w-0">
        <span className="font-medium">{shortageLabel(s)}</span>
        {shortageVariant(s) && (
          <span className="text-muted-foreground"> · {shortageVariant(s)}</span>
        )}
        <span className="ml-2 font-mono text-xs text-muted-foreground">{s.ssSku}</span>
        {s.note && (
          <span className="ml-2 max-w-[16rem] truncate align-bottom text-xs italic text-muted-foreground">
            “{s.note}”
          </span>
        )}
      </span>
      <span className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground">
          inv {s.invoice}
          {s.boxNumber != null && ` · box ${s.boxNumber}`}
        </span>
        {s.poNumber ? (
          <span className="rounded border border-amber-300 px-1.5 py-0.5 font-mono text-[11px] text-amber-800 dark:border-amber-800 dark:text-amber-300">
            PO {s.poNumber}
          </span>
        ) : (
          <span className="text-xs italic text-muted-foreground">no PO</span>
        )}
        <span className="text-xs text-muted-foreground" title={fmtDate(s.recordedAt) ?? undefined}>
          {claimAge(s.recordedAt)}
        </span>
        {s.residualShort < s.shortQty && (
          <span className="text-xs text-emerald-700 dark:text-emerald-400">
            {s.shortQty - s.residualShort} made good
          </span>
        )}
        <span className="tabular-nums text-amber-700 dark:text-amber-400">
          {s.receivedQty}/{s.claimedQty} · short {s.residualShort}
        </span>
        {canWrite && (
          <span className="flex items-center gap-1.5">
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              max={s.claimedQty}
              value={received}
              onChange={(e) => onEdit(e.target.value)}
              onFocus={(e) => e.currentTarget.select()}
              aria-label={`Corrected received for invoice ${s.invoice} line ${s.lineNumber}`}
              className="w-16 text-right tabular-nums"
            />
            <Button
              size="sm"
              variant="outline"
              disabled={correct.isPending || received === s.receivedQty}
              onClick={() => correct.mutate(received)}
            >
              Save
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={correct.isPending || s.receivedQty >= s.claimedQty}
              onClick={() => setMiscountOpen(true)}
            >
              {correct.isPending ? <Loader2 className="size-3.5 animate-spin" /> : null}
              All arrived (miscount)…
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSettleOpen((v) => !v)}
              disabled={settleBlocked}
              title={
                settleBlocked
                  ? 'This invoice is still being received box by box — settle once every box is in'
                  : undefined
              }
            >
              Made good…
            </Button>
          </span>
        )}
      </span>
      {willNeverAutoClear(s) ? (
        <span className="w-full text-xs italic text-muted-foreground">
          {s.poNumber
            ? 'Won’t auto-clear — its receipt carries no S&S order number. Scan the replacement in as usual, then settle it with Made good…'
            : 'No FSP PO — a replacement can’t be matched to this claim automatically. Scan it in as usual, then settle with Made good…'}
        </span>
      ) : s.timeBlockedInvoice ? (
        <span className="w-full text-xs italic text-muted-foreground">
          Won’t auto-clear — inv {s.timeBlockedInvoice} looks like this claim’s replacement but was
          scanned before the short was recorded. If it was, settle with Made good… (it’s
          pre-filled).
        </span>
      ) : null}
      <Dialog open={miscountOpen} onOpenChange={(o) => !o && setMiscountOpen(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>All {s.claimedQty} arrived after all?</DialogTitle>
            <DialogDescription>
              Inv {s.invoice} line {s.lineNumber} · currently {s.receivedQty} of {s.claimedQty}.
            </DialogDescription>
          </DialogHeader>
          {/* What the correction ADDS is claimedQty − receivedQty, NOT residualShort. residualShort
              is what's still owed AFTER PO reconciliation, so it shrinks when a make-good already
              arrived under the same PO — quoting it would understate the spend by exactly the
              amount already received, on the button most likely to double-count. */}
          <p className="text-sm">
            This sets received to {s.claimedQty}, adding{' '}
            <strong>
              {addedByMiscount} {addedByMiscount === 1 ? 'piece' : 'pieces'}
            </strong>{' '}
            to stock, and closes the claim.
          </p>
          {alreadyMadeGood > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertTitle>
                {alreadyMadeGood} of those {alreadyMadeGood === 1 ? 'pieces has' : 'pieces have'}{' '}
                already arrived
              </AlertTitle>
              <AlertDescription>
                A make-good under {s.poNumber ?? 'the same PO'} already topped this line up, so only{' '}
                {s.residualShort} {s.residualShort === 1 ? 'piece is' : 'pieces are'} still owed.
                Confirming here counts the other {alreadyMadeGood} a second time — settle with{' '}
                <strong>Made good…</strong> instead.
              </AlertDescription>
            </Alert>
          )}
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertTitle>Only if they were in this box</AlertTitle>
            <AlertDescription>
              Use this when the pieces were physically here and got mis-counted. If a replacement
              shipped separately, scan it in and settle with <strong>Made good…</strong> instead —
              this would count those pieces twice.
            </AlertDescription>
          </Alert>
          {correct.isError && (
            <Alert variant="destructive">
              <AlertTitle>Nothing was corrected</AlertTitle>
              <AlertDescription>
                {correct.error instanceof Error ? correct.error.message : 'Correction failed.'}
              </AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMiscountOpen(false)}
              disabled={correct.isPending}
            >
              Keep the claim
            </Button>
            <Button
              variant="destructive"
              disabled={correct.isPending}
              onClick={() =>
                // Close only on SUCCESS: closing first made a failed correction silent.
                correct.mutate(s.claimedQty, { onSuccess: () => setMiscountOpen(false) })
              }
            >
              {correct.isPending ? <Loader2 className="size-3.5 animate-spin" /> : null}
              Yes, all arrived
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {settleOpen && canWrite && (
        <div className="flex w-full flex-wrap items-center justify-end gap-2 border-t pt-2">
          <span className="text-xs text-muted-foreground">
            Settle {s.residualShort} — no stock moves:
          </span>
          <select
            aria-label={`Settle reason for invoice ${s.invoice} line ${s.lineNumber}`}
            className="h-9 rounded-md border bg-background px-2 text-sm"
            value={reason}
            onChange={(e) => setReason(e.target.value as SettleReason)}
          >
            {SETTLE_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <Input
            value={replacementInvoice}
            onChange={(e) => setReplacementInvoice(e.target.value)}
            placeholder="replacement inv # (optional)"
            aria-label={`Replacement invoice for invoice ${s.invoice} line ${s.lineNumber}`}
            className="w-44"
          />
          <Button size="sm" disabled={settle.isPending} onClick={() => settle.mutate()}>
            {settle.isPending ? <Loader2 className="size-3.5 animate-spin" /> : null}
            Settle claim
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={settle.isPending}
            onClick={() => setSettleOpen(false)}
          >
            Cancel
          </Button>
        </div>
      )}
      {(correct.isError || settle.isError) && (
        <span className="w-full text-right text-xs text-destructive">
          {correct.error instanceof Error
            ? correct.error.message
            : settle.error instanceof Error
              ? settle.error.message
              : 'Action failed.'}
        </span>
      )}
    </li>
  );
}

/**
 * The "recently settled" undo worklist — claims closed via Made good / credit / write-off, newest first.
 * Ships alongside the settle action so a wrong settle is always reversible (a settled claim leaves the
 * shortages list, so this is the only place to find + undo it). Hidden when empty.
 */
/**
 * One merged "Recent activity — undo" panel (P3, F5-5): every reversible recent action — miscount
 * corrections and settled claims — in one collapsed block instead of two stacked panels. Each row
 * keeps its own Undo; the server computes what "undo" restores, so there are no numbers to remember.
 * Hidden when both lists are empty.
 */
function RecentActivityPanel() {
  const client = useApiClient();
  const qc = useQueryClient();
  const { state } = useAuth();
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;

  const corrections = useQuery({
    queryKey: CORRECTIONS_KEY,
    queryFn: async () => {
      const { data, error } = await client.GET('/receiving/recent-corrections');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load recent corrections.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });
  const resolved = useQuery({
    queryKey: RESOLVED_KEY,
    queryFn: async () => {
      const { data, error } = await client.GET('/receiving/resolved');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load settled claims.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const undo = useMutation({
    mutationFn: async (r: RecentCorrection) => {
      const { data, error } = await client.POST('/receiving/correct', {
        body: {
          invoice: r.invoice,
          lineNumber: r.lineNumber,
          receivedQty: r.previousReceivedQty,
          clientToken: crypto.randomUUID(),
        },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Undo failed.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    onSuccess: () => {
      invalidateInventory(qc);
      qc.invalidateQueries({ queryKey: SHORTAGES_KEY });
      qc.invalidateQueries({ queryKey: CORRECTIONS_KEY });
    },
  });

  const unsettle = useMutation({
    mutationFn: async (r: ResolvedShortage) => {
      const { data, error } = await client.POST('/receiving/resolve/reverse', {
        body: { invoice: r.invoice, lineNumber: r.lineNumber, ssSku: r.ssSku },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Undo failed.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    onSuccess: () => {
      invalidateInventory(qc);
      qc.invalidateQueries({ queryKey: SHORTAGES_KEY });
      qc.invalidateQueries({ queryKey: RESOLVED_KEY });
    },
  });

  const correctionRows = corrections.data?.corrections ?? [];
  const settledRows = resolved.data?.resolved ?? [];
  const total = correctionRows.length + settledRows.length;
  if (total === 0) return null;

  const identity = (r: {
    brandName?: string;
    styleName?: string;
    colorName?: string;
    sizeName?: string;
    ssSku: string;
  }) => (
    <>
      <span className="font-medium">
        {[r.brandName, r.styleName].filter(Boolean).join(' ') || r.ssSku}
      </span>
      {[r.colorName, r.sizeName].filter(Boolean).join(' · ') && (
        <span className="text-muted-foreground">
          {' · '}
          {[r.colorName, r.sizeName].filter(Boolean).join(' · ')}
        </span>
      )}
    </>
  );

  return (
    <details className="rounded-lg border text-sm">
      <summary className="cursor-pointer px-4 py-2.5 font-medium text-muted-foreground">
        Recent activity — undo ({total})
      </summary>

      {correctionRows.length > 0 && (
        <>
          <div className="border-t px-4 pb-1 pt-2 text-xs font-medium text-muted-foreground">
            Count corrections
          </div>
          <ul className="divide-y px-1 pb-1">
            {correctionRows.map((r) => (
              <li
                key={`${r.ssSku}#${r.invoice}#${r.lineNumber}`}
                className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2"
              >
                <span className="min-w-0">
                  {identity(r)}
                  <span className="ml-2 text-xs text-muted-foreground">
                    received {r.previousReceivedQty} → {r.receivedQty} (of {r.claimedQty})
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">inv {r.invoice}</span>
                  {canWrite && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7"
                      title={`Undo — set received back to ${r.previousReceivedQty}`}
                      disabled={undo.isPending}
                      onClick={() => undo.mutate(r)}
                    >
                      <Undo2 className="size-3.5" />
                      Undo → {r.previousReceivedQty}
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {undo.isError && (
            <p className="px-4 pb-2 text-right text-xs text-destructive">
              {undo.error instanceof Error ? undo.error.message : 'Undo failed.'}
            </p>
          )}
        </>
      )}

      {settledRows.length > 0 && (
        <>
          <div className="border-t px-4 pb-1 pt-2 text-xs font-medium text-muted-foreground">
            Settled claims
          </div>
          <ul className="divide-y px-1 pb-1">
            {settledRows.map((r) => (
              <li
                key={`${r.ssSku}#${r.invoice}#${r.lineNumber}`}
                className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2"
              >
                <span className="min-w-0">
                  {identity(r)}
                  <span className="ml-2 text-xs text-muted-foreground">
                    {REASON_LABEL[r.reason]} · {r.shortQty} piece{r.shortQty === 1 ? '' : 's'}
                    {r.writtenOffQty ? ` · wrote off ${r.writtenOffQty} on ${r.poNumber}` : ''}
                    {r.replacementInvoice ? ` · repl inv ${r.replacementInvoice}` : ''}
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">inv {r.invoice}</span>
                  {canWrite && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7"
                      disabled={unsettle.isPending}
                      onClick={() => unsettle.mutate(r)}
                    >
                      <Undo2 className="size-3.5" />
                      Undo
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {unsettle.isError && (
            <p className="px-4 pb-2 text-right text-xs text-destructive">
              {unsettle.error instanceof Error ? unsettle.error.message : 'Undo failed.'}
            </p>
          )}
        </>
      )}
    </details>
  );
}
